// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

// D-CRX-84 round 2 — the Enrollment queue's Status cell (the prescription's stage, the stored enrollment
// record one hover away) and its row action. "Manual status" is deliberately NOT a dropdown on
// enrollment_status (that unlocks consent send, the DocuSign gate, POT and LC-03 and would bypass clinical
// review — board Q5): the action runs the real workflow step for the row's stage, and the server audits it.

import { useState } from 'react'
import type { CSSProperties } from 'react'
import type { CareRxEnrollmentQueueRow } from '../../api/client'
import { friendlyError } from '../../api/errors'
import StatusChip from '../../components/ui/StatusChip'
import ConfirmDialog from '../../components/ui/ConfirmDialog'
import type { StatusTone } from '../../api/status'
import { useRecheckLabs, useSendToLabs } from '../../hooks/queries/carerxEnrollmentQueue'
import { enrollmentRecordTip, stageAction, stageText } from './enrollmentStage'

const STAGE_TONE: Record<NonNullable<CareRxEnrollmentQueueRow['stage']>, StatusTone> = {
  on_hold: 'red', renewal: 'blue', pending_labs: 'amber', pending_review: 'amber', pending_review_callback: 'amber', approved: 'green',
}

type Row = Pick<CareRxEnrollmentQueueRow, 'stage' | 'enrollment_status' | 'therapy_id' | 'drug_name'>

/** The Status cell. A row with no stage (no open prescription, or a tenant without migs 310/313) shows the
 *  stored enrollment status exactly as before. */
export function EnrollmentStageStatus({ row }: { row: Row }) {
  return (
    <span title={enrollmentRecordTip(row)} data-stage={row.stage ?? undefined}>
      <StatusChip status={row.stage ?? row.enrollment_status} label={stageText(row)}
        tone={row.stage ? STAGE_TONE[row.stage] : undefined} />
      {/* The stored record, visible without hover (touch screens have none) — only when the chip shows a stage. */}
      {row.stage && <span style={{ display: 'block', fontSize: 11, color: 'var(--color-text-mute)', marginTop: 2 }}>
        {enrollmentRecordTip(row)}</span>}
    </span>
  )
}

/** "Send to labs" on a Pending-clinical-review row, "Re-check labs" on a Pending-labs row; nothing otherwise. */
export function EnrollmentStageAction({ row, buttonStyle, onDone }: {
  row: Row; buttonStyle: CSSProperties; onDone: (message: string, ok: boolean) => void
}) {
  const action = stageAction(row)
  const what = row.drug_name || 'this prescription'
  const [confirming, setConfirming] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Hook-level callbacks: they still fire after this row unmounts (it moves chip on success).
  const send = useSendToLabs({
    onSuccess: r => { setConfirming(false); onDone(r.unchanged ? `${what} is already waiting on labs.` : `${what} sent to the Lab Tracker.`, true) },
    onError: e => setError(friendlyError(e)),
  })
  const recheck = useRecheckLabs({
    onSuccess: r => onDone(r.in_tracker
      ? `${what}: still waiting on ${r.missing_labs.length} lab${r.missing_labs.length === 1 ? '' : 's'} (${r.missing_labs.join(', ')}).`
      : `${what}: labs complete — moved to Clinical review.`, true),
    onError: e => onDone(friendlyError(e), false),
  })
  if (!action || !row.therapy_id) return null
  const therapyId = row.therapy_id

  if (action === 'recheck_labs') {
    return (
      <button style={buttonStyle} disabled={recheck.isPending}
        title="Re-check this prescription's required labs against what is on file"
        onClick={() => recheck.mutate(therapyId)}>
        {recheck.isPending ? 'Checking…' : 'Re-check labs'}
      </button>
    )
  }
  return (
    <>
      <button style={buttonStyle} onClick={() => { setError(null); setConfirming(true) }}>Send to labs</button>
      <ConfirmDialog open={confirming} title="Send to labs"
        body={<>
          <b>{what}</b> goes back to the Lab Tracker, and its clinical review starts again once the labs are
          collected. The enrollment record is not changed.
        </>}
        confirmLabel="Send to labs" busy={send.isPending} busyLabel="Sending…" error={error}
        onCancel={() => setConfirming(false)}
        onConfirm={() => send.mutate(therapyId)} />
    </>
  )
}
